import React, { useEffect, useState, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sparkles, Heart, Scissors, Users, Accessibility, Star, MapPin, Phone, Clock, Globe, Check, ChevronDown, ChevronLeft, ChevronRight, Quote, Frown, Lightbulb, Diamond, Leaf, Trophy, Target, Lock, Megaphone, ArrowRight, Zap, HelpCircle } from 'lucide-react';
import { LanguageProvider, useLanguage } from './components/LanguageContext';
import { IntroAnimation } from './components/IntroAnimation';
import { LanguageSelector } from './components/LanguageSelector';
import { BookingToast } from './components/BookingToast';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/nrmTFaPw3fdgTnDyCxNNzV/Captura_de_pantalla_2026-05-26_231223.png';
const BANNER_URL = 'https://cdn.magicpatterns.com/uploads/ay3vA9iB1cH2Mf867gqiAC/Banner1.1.png';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
function MainApp() {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [logoBgColor, setLogoBgColor] = useState<string>('#A1CCE0');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [slideDirection, setSlideDirection] = useState(1);
  const gallerySlides = t('gallery.slides') as any[];
  const faqs = t('faq.items') as any[];
  useEffect(() => {
    if (isCarouselPaused) return;
    const interval = setInterval(() => {
      setSlideDirection(1);
      setCurrentSlide(prev => (prev + 1) % gallerySlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isCarouselPaused, gallerySlides.length]);
  const nextSlide = () => {
    setSlideDirection(1);
    setCurrentSlide(prev => (prev + 1) % gallerySlides.length);
  };
  const prevSlide = () => {
    setSlideDirection(-1);
    setCurrentSlide(prev => (prev - 1 + gallerySlides.length) % gallerySlides.length);
  };
  const goToSlide = (i: number) => {
    setSlideDirection(i > currentSlide ? 1 : -1);
    setCurrentSlide(i);
  };
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = LOGO_URL;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);
      try {
        const pixel = ctx.getImageData(2, 2, 1, 1).data;
        setLogoBgColor(`rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`);
      } catch (e) {
        // CORS fallback
      }
    };
  }, []);
  return <div className="min-h-screen font-sans text-brand-brown bg-brand-cream selection:bg-brand-terracotta selection:text-white" data-id="element-1">
      {/* HEADER */}
      <header style={{
      backgroundColor: logoBgColor
    }} className="sticky top-0 z-50 w-full shadow-sm" data-id="element-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3">
          <div className="flex justify-between items-center h-24" data-id="element-4">
            <div className="flex-shrink-0 flex items-center" data-id="element-5">
              <img src={LOGO_URL} alt="Conexión Salon and Spa Logo" className="h-16 w-auto object-contain" data-id="element-6" />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex space-x-6 lg:space-x-8 items-center" data-id="element-7">
              <a href="#services" className="text-brand-brown hover:text-brand-terracotta font-medium transition-colors" data-id="element-8">
                {t('nav.services')}
              </a>
              <a href="#about" className="text-brand-brown hover:text-brand-terracotta font-medium transition-colors" data-id="element-9">
                {t('nav.about')}
              </a>
              <a href="#testimonials" className="text-brand-brown hover:text-brand-terracotta font-medium transition-colors" data-id="element-10">
                {t('nav.testimonials')}
              </a>
              <a href="#faq" className="text-brand-brown hover:text-brand-terracotta font-medium transition-colors" data-id="element-11">
                {t('nav.faq')}
              </a>

              {/* Language Toggle */}
              <button onClick={() => setLang(lang === 'en' ? 'es' : 'en')} className="flex items-center gap-1 text-sm font-bold text-brand-brown hover:text-brand-terracotta transition-colors px-2" data-id="element-12">
                <span className={lang === 'en' ? 'opacity-100' : 'opacity-40'} data-id="element-13">
                  EN
                </span>
                <span className="opacity-30" data-id="element-14">|</span>
                <span className={lang === 'es' ? 'opacity-100' : 'opacity-40'} data-id="element-15">
                  ES
                </span>
              </button>

              <a href="tel:+17732815800" className="flex items-center gap-2 bg-white/40 hover:bg-white/70 backdrop-blur-sm text-brand-brown px-4 py-2.5 rounded-sm font-medium transition-all duration-300 border border-white/60 group" data-id="element-16">
                <Phone className="w-4 h-4 text-brand-terracotta group-hover:animate-pulse" data-id="element-17" />
                <span className="hidden lg:inline" data-id="element-18">773-281-5800</span>
                <span className="lg:hidden" data-id="element-19">{t('nav.call')}</span>
              </a>
              <a href="#book" className="bg-brand-terracotta hover:bg-brand-terracotta-hover text-white px-6 py-3 rounded-sm font-medium transition-colors shadow-sm" data-id="element-20">
                {t('nav.bookNow')}
              </a>
            </nav>

            {/* Mobile actions */}
            <div className="md:hidden flex items-center gap-3" data-id="element-21">
              <button onClick={() => setLang(lang === 'en' ? 'es' : 'en')} className="flex items-center gap-1 text-sm font-bold text-brand-brown hover:text-brand-terracotta transition-colors" data-id="element-22">
                <span className={lang === 'en' ? 'opacity-100' : 'opacity-40'} data-id="element-23">
                  EN
                </span>
                <span className="opacity-30" data-id="element-24">|</span>
                <span className={lang === 'es' ? 'opacity-100' : 'opacity-40'} data-id="element-25">
                  ES
                </span>
              </button>
              <a href="tel:+17732815800" aria-label="Call Conexión" className="w-10 h-10 flex items-center justify-center bg-brand-terracotta text-white rounded-full shadow-sm hover:bg-brand-terracotta-hover transition-colors" data-id="element-26">
                <Phone className="w-4 h-4" data-id="element-27" />
              </a>
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-brand-brown hover:text-brand-terracotta focus:outline-none p-1" data-id="element-28">
                {isMobileMenuOpen ? <X size={28} data-id="element-29" /> : <Menu size={28} data-id="element-30" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence data-id="element-31">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} style={{
          backgroundColor: logoBgColor
        }} className="md:hidden border-t border-brand-brown/10 overflow-hidden" data-id="element-32">
              <div className="px-4 pt-2 pb-6 space-y-2 flex flex-col" data-id="element-33">
                {[{
              href: '#services',
              label: t('nav.services')
            }, {
              href: '#about',
              label: t('nav.about')
            }, {
              href: '#testimonials',
              label: t('nav.testimonials')
            }, {
              href: '#location',
              label: t('nav.location')
            }, {
              href: '#faq',
              label: t('nav.faq')
            }].map(item => <a key={item.href} href={item.href} onClick={e => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              const targetId = item.href.replace('#', '');
              setTimeout(() => {
                const el = document.getElementById(targetId);
                if (el) el.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }, 300);
            }} className="block px-3 py-3 text-brand-brown font-medium hover:bg-white/30 active:bg-white/50 rounded-sm transition-colors" data-id="element-34">
                    {item.label}
                  </a>)}
                <a href="#book" onClick={e => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              setTimeout(() => {
                const el = document.getElementById('book');
                if (el) el.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }, 300);
            }} className="block px-3 py-3 mt-4 text-center bg-brand-terracotta hover:bg-brand-terracotta-hover text-white rounded-sm font-medium transition-colors" data-id="element-35">
                  {t('nav.bookNow')}
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      <main data-id="element-36">
        {/* HERO SECTION */}
        <section className="relative w-full" data-id="element-37">
          <div className="w-full h-[60vh] md:h-[75vh] relative" data-id="element-38">
            <img src={BANNER_URL} alt="Conexión Salon and Spa" className="w-full h-full object-cover" data-id="element-39" />
            <div className="absolute inset-0 bg-brand-brown/20 mix-blend-multiply" data-id="element-40"></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 md:-mt-24 relative z-10" data-id="element-41">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="bg-brand-cream p-8 md:p-12 shadow-xl text-center border-t-4 border-brand-terracotta" data-id="element-42">
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-brand-brown mb-6" data-id="element-43">
                {t('hero.headline')}
              </h1>
              <p className="text-lg md:text-xl text-brand-brown/80 mb-8 max-w-2xl mx-auto" data-id="element-44">
                {t('hero.subheadline')}
              </p>
              <a href="#book" className="inline-flex items-center justify-center bg-brand-terracotta hover:bg-brand-terracotta-hover text-white px-8 py-4 rounded-sm font-medium text-lg transition-colors shadow-md group" data-id="element-45">
                {t('hero.cta')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-46" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* OPENING LEAD */}
        <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8" data-id="element-47">
          <div className="max-w-3xl mx-auto space-y-8 text-lg md:text-xl leading-relaxed text-brand-brown/90" data-id="element-48">
            <FadeIn data-id="element-49">
              <p className="font-serif text-2xl md:text-3xl italic text-brand-brown text-center mb-12" data-id="element-50">
                {t('lead.p1')}
              </p>
            </FadeIn>
            <FadeIn delay={0.1} data-id="element-51">
              <p data-id="element-52">{t('lead.p2')}</p>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-53">
              <p data-id="element-54">{t('lead.p3')}</p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-55">
              <p className="font-medium text-center py-4" data-id="element-56">{t('lead.p4')}</p>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-57">
              <p data-id="element-58">{t('lead.p5')}</p>
            </FadeIn>
            <FadeIn delay={0.5} data-id="element-59">
              <p className="font-serif text-2xl md:text-3xl font-medium text-brand-terracotta text-center pt-8" data-id="element-60">
                {t('lead.p6')}
              </p>
            </FadeIn>
          </div>
        </section>

        {/* FULL SALES LETTER START */}
        <section className="py-16 bg-white px-4 sm:px-6 lg:px-8" data-id="element-61">
          <div className="max-w-3xl mx-auto" data-id="element-62">
            <FadeIn data-id="element-63">
              <h2 className="font-serif text-3xl md:text-4xl text-center mb-12" data-id="element-64">
                {t('letter.headline')}
              </h2>
              <div className="space-y-6 text-lg text-brand-brown/80" data-id="element-65">
                <p data-id="element-66">{t('letter.p1')}</p>
                <p className="font-medium text-brand-brown text-xl" data-id="element-67">
                  {t('letter.p2')}
                </p>
                <p data-id="element-68">{t('letter.p3')}</p>
                <p data-id="element-69">{t('letter.p4')}</p>
                <p className="font-serif text-2xl text-brand-terracotta italic text-center py-6" data-id="element-70">
                  {t('letter.p5')}
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* THE TRAP */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-cream" data-id="element-71">
          <div className="max-w-3xl mx-auto" data-id="element-72">
            <FadeIn data-id="element-73">
              <div className="flex items-center justify-center mb-8" data-id="element-74">
                <Frown className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-75" />
                <h2 className="font-serif text-3xl md:text-4xl" data-id="element-76">
                  {t('trap.headline')}
                </h2>
              </div>
              <p className="text-lg mb-8 text-center" data-id="element-77">{t('trap.p1')}</p>
              <ul className="space-y-4 mb-12" data-id="element-78">
                {(t('trap.items') as string[]).map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start bg-white p-4 rounded-sm shadow-sm" data-id="element-79">
                    <Check className="w-6 h-6 text-brand-terracotta mr-4 flex-shrink-0 mt-0.5" data-id="element-80" />
                    <span className="text-lg" data-id="element-81">{item}</span>
                  </motion.li>)}
              </ul>
              <div className="text-center space-y-4 text-lg" data-id="element-82">
                <p className="font-medium" data-id="element-83">{t('trap.p2')}</p>
                <p data-id="element-84">{t('trap.p3')}</p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* THE REAL REASON */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-85">
          <div className="max-w-3xl mx-auto" data-id="element-86">
            <FadeIn data-id="element-87">
              <div className="flex items-center justify-center mb-8" data-id="element-88">
                <Lightbulb className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-89" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-90">
                  {t('reason.headline')}
                </h2>
              </div>
              <div className="space-y-6 text-lg text-brand-brown/80" data-id="element-91">
                <p data-id="element-92">{t('reason.p1')}</p>
                <p className="font-medium text-xl text-brand-brown" data-id="element-93">
                  {t('reason.p2')}
                </p>
                <p data-id="element-94">{t('reason.p3')}</p>
                <p data-id="element-95">{t('reason.p4')}</p>
                <p className="font-serif text-2xl text-brand-terracotta text-center pt-8" data-id="element-96">
                  {t('reason.p5')}
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* INTRODUCING */}
        <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-blue/20" data-id="element-97">
          <div className="max-w-3xl mx-auto text-center" data-id="element-98">
            <FadeIn data-id="element-99">
              <div className="flex items-center justify-center mb-6" data-id="element-100">
                <Sparkles className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-101" />
                <h2 className="font-serif text-3xl md:text-4xl" data-id="element-102">
                  {t('intro.headline')}
                </h2>
              </div>
              <p className="text-xl font-medium mb-8" data-id="element-103">{t('intro.p1')}</p>
              <p className="font-serif text-2xl italic mb-8" data-id="element-104">{t('intro.p2')}</p>
              <div className="space-y-6 text-lg text-left text-brand-brown/80" data-id="element-105">
                <p data-id="element-106">{t('intro.p3')}</p>
                <p data-id="element-107">{t('intro.p4')}</p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* THE STORY */}
        <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-108">
          <div className="max-w-6xl mx-auto" data-id="element-109">
            <FadeIn data-id="element-110">
              <div className="flex items-center justify-center mb-12 md:mb-16" data-id="element-111">
                <Users className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-112" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-113">
                  {t('story.headline')}
                </h2>
              </div>
            </FadeIn>
            <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center" data-id="element-114">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} className="md:col-span-5 relative" data-id="element-115">
                <div className="relative" data-id="element-116">
                  <div className="absolute -top-4 -left-4 w-full h-full bg-brand-terracotta/15 rounded-sm hidden md:block" data-id="element-117"></div>
                  <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-brand-blue rounded-sm hidden md:block" data-id="element-118"></div>
                  <div className="relative overflow-hidden rounded-sm shadow-2xl" data-id="element-119">
                    <img src="https://cdn.magicpatterns.com/uploads/fZu9eEH4P6o6DXwJToL4ZS/margarita_1.jpg" alt="Margarita" className="w-full h-auto object-cover aspect-[3/4] object-top" data-id="element-120" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-brown/40 via-transparent to-transparent" data-id="element-121"></div>
                  </div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.4,
                  duration: 0.6
                }} className="absolute -bottom-6 left-6 right-6 md:left-1/2 md:-translate-x-1/2 md:w-max md:right-auto bg-white shadow-lg px-6 py-3 rounded-sm border-l-2 border-brand-terracotta" data-id="element-122">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-terracotta font-medium mb-0.5" data-id="element-123">
                      {t('story.tag')}
                    </p>
                    <p className="font-serif text-xl text-brand-brown" data-id="element-124">
                      Margarita
                    </p>
                  </motion.div>
                </div>
              </motion.div>
              <div className="md:col-span-7 mt-12 md:mt-0" data-id="element-125">
                <FadeIn delay={0.2} data-id="element-126">
                  <div className="space-y-6 text-lg text-brand-brown/80" data-id="element-127">
                    <p data-id="element-128">{t('story.p1')}</p>
                    <p data-id="element-129">{t('story.p2')}</p>
                    <p data-id="element-130">{t('story.p3')}</p>
                    <blockquote className="border-l-4 border-brand-terracotta pl-6 py-4 my-8 bg-brand-cream/50 italic font-serif text-2xl text-brand-brown" data-id="element-131">
                      {t('story.quote')}
                    </blockquote>
                    <p data-id="element-132">{t('story.p4')}</p>
                  </div>
                </FadeIn>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-cream" data-id="element-133">
          <div className="max-w-5xl mx-auto" data-id="element-134">
            <FadeIn data-id="element-135">
              <div className="flex items-center justify-center mb-12" data-id="element-136">
                <Diamond className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-137" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-138">
                  {t('services.headline')}
                </h2>
              </div>
            </FadeIn>
            <div className="grid md:grid-cols-2 gap-12" data-id="element-139">
              <FadeIn delay={0.1} data-id="element-140">
                <div className="bg-white p-8 shadow-sm rounded-sm h-full border-t-4 border-brand-blue" data-id="element-141">
                  <div className="flex items-center mb-6" data-id="element-142">
                    <Scissors className="w-6 h-6 text-brand-terracotta mr-3" data-id="element-143" />
                    <h3 className="font-serif text-2xl font-medium" data-id="element-144">
                      {t('services.hairTitle')}
                    </h3>
                  </div>
                  <div className="space-y-6" data-id="element-145">
                    {(t('services.hair') as any[]).map((item, i) => <div key={i} className="border-b border-brand-cream pb-4 last:border-0 last:pb-0" data-id="element-146">
                        <h4 className="font-medium text-lg mb-1" data-id="element-147">{item.s}</h4>
                        <p className="text-brand-brown/70" data-id="element-148">{item.d}</p>
                      </div>)}
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={0.2} data-id="element-149">
                <div className="bg-white p-8 shadow-sm rounded-sm h-full border-t-4 border-brand-terracotta" data-id="element-150">
                  <div className="flex items-center mb-6" data-id="element-151">
                    <Leaf className="w-6 h-6 text-brand-terracotta mr-3" data-id="element-152" />
                    <h3 className="font-serif text-2xl font-medium" data-id="element-153">
                      {t('services.wellnessTitle')}
                    </h3>
                  </div>
                  <div className="space-y-6" data-id="element-154">
                    {(t('services.wellness') as any[]).map((item, i) => <div key={i} className="border-b border-brand-cream pb-4 last:border-0 last:pb-0" data-id="element-155">
                        <h4 className="font-medium text-lg mb-1" data-id="element-156">{item.s}</h4>
                        <p className="text-brand-brown/70" data-id="element-157">{item.d}</p>
                      </div>)}
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* OUR WORK CAROUSEL */}
        <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden" data-id="element-158">
          <div className="max-w-7xl mx-auto" data-id="element-159">
            <FadeIn data-id="element-160">
              <div className="flex items-center justify-center mb-4" data-id="element-161">
                <Sparkles className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-162" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-163">
                  {t('gallery.headline')}
                </h2>
              </div>
              <p className="text-center text-lg text-brand-brown/70 mb-16 max-w-2xl mx-auto" data-id="element-164">
                {t('gallery.subtitle')}
              </p>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-165">
              <div className="relative" onMouseEnter={() => setIsCarouselPaused(true)} onMouseLeave={() => setIsCarouselPaused(false)} data-id="element-166">
                <div className="relative w-full aspect-[4/5] sm:aspect-[16/10] md:aspect-[16/9] max-h-[70vh] overflow-hidden rounded-sm bg-brand-brown/5 shadow-2xl" data-id="element-167">
                  <AnimatePresence initial={false} custom={slideDirection} mode="popLayout" data-id="element-168">
                    <motion.div key={currentSlide} custom={slideDirection} initial={{
                    opacity: 0,
                    x: slideDirection * 60,
                    scale: 1.02
                  }} animate={{
                    opacity: 1,
                    x: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    x: slideDirection * -60,
                    scale: 1.02
                  }} transition={{
                    opacity: {
                      duration: 0.6
                    },
                    x: {
                      duration: 0.7,
                      ease: [0.32, 0.72, 0, 1]
                    },
                    scale: {
                      duration: 0.7
                    }
                  }} className="absolute inset-0" data-id="element-169">
                      <img src={gallerySlides[currentSlide].src} alt={gallerySlides[currentSlide].title} className="w-full h-full object-cover object-center" data-id="element-170" />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-brown via-brand-brown/40 to-transparent opacity-80" data-id="element-171"></div>
                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.2,
                      duration: 0.5
                    }} className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-16" data-id="element-172">
                        <div className="max-w-2xl" data-id="element-173">
                          <p className="text-brand-blue text-xs md:text-sm uppercase tracking-[0.25em] mb-3 font-medium" data-id="element-174">
                            {gallerySlides[currentSlide].label}
                          </p>
                          <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight" data-id="element-175">
                            {gallerySlides[currentSlide].title}
                          </h3>
                          <p className="text-white/80 text-sm md:text-lg max-w-xl" data-id="element-176">
                            {gallerySlides[currentSlide].description}
                          </p>
                        </div>
                      </motion.div>
                    </motion.div>
                  </AnimatePresence>
                  <div className="absolute top-6 right-6 md:top-8 md:right-8 text-white/90 font-serif text-sm md:text-base tracking-widest" data-id="element-177">
                    <span className="text-2xl md:text-3xl" data-id="element-178">
                      {String(currentSlide + 1).padStart(2, '0')}
                    </span>
                    <span className="opacity-60 mx-2" data-id="element-179">/</span>
                    <span className="opacity-60" data-id="element-180">
                      {String(gallerySlides.length).padStart(2, '0')}
                    </span>
                  </div>
                  <button onClick={prevSlide} className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/20 hover:bg-brand-terracotta backdrop-blur-md flex items-center justify-center text-white transition-all duration-300 hover:scale-110 border border-white/30" data-id="element-181">
                    <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-182" />
                  </button>
                  <button onClick={nextSlide} className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/20 hover:bg-brand-terracotta backdrop-blur-md flex items-center justify-center text-white transition-all duration-300 hover:scale-110 border border-white/30" data-id="element-183">
                    <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-184" />
                  </button>
                </div>
                <div className="mt-8 flex flex-col items-center gap-6" data-id="element-185">
                  <div className="hidden md:flex gap-3 items-center" data-id="element-186">
                    {gallerySlides.map((slide, i) => <button key={i} onClick={() => goToSlide(i)} className={`relative overflow-hidden rounded-sm transition-all duration-500 ${currentSlide === i ? 'w-24 h-20 ring-2 ring-brand-terracotta ring-offset-2' : 'w-16 h-20 opacity-50 hover:opacity-100'}`} data-id="element-187">
                        <img src={slide.src} alt={slide.title} className="w-full h-full object-cover object-center" data-id="element-188" />
                      </button>)}
                  </div>
                  <div className="flex md:hidden gap-2" data-id="element-189">
                    {gallerySlides.map((_, i) => <button key={i} onClick={() => goToSlide(i)} className={`h-2 rounded-full transition-all duration-300 ${currentSlide === i ? 'w-8 bg-brand-terracotta' : 'w-2 bg-brand-brown/30'}`} data-id="element-190" />)}
                  </div>
                  <div className="w-full max-w-md h-px bg-brand-brown/10 relative overflow-hidden" data-id="element-191">
                    <motion.div key={`${currentSlide}-${isCarouselPaused}`} initial={{
                    scaleX: 0
                  }} animate={{
                    scaleX: isCarouselPaused ? 0 : 1
                  }} transition={{
                    duration: 5,
                    ease: 'linear'
                  }} style={{
                    transformOrigin: 'left'
                  }} className="h-full bg-brand-terracotta" data-id="element-192" />
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* INCLUSION */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-blue/30" data-id="element-193">
          <div className="max-w-3xl mx-auto text-center" data-id="element-194">
            <FadeIn data-id="element-195">
              <div className="flex items-center justify-center mb-6" data-id="element-196">
                <Heart className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-197" />
                <h2 className="font-serif text-3xl md:text-4xl" data-id="element-198">
                  {t('inclusion.headline')}
                </h2>
              </div>
              <p className="text-lg mb-8" data-id="element-199">{t('inclusion.p1')}</p>
              <p className="font-serif text-2xl italic text-brand-terracotta mb-10" data-id="element-200">
                {t('inclusion.p2')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-left mb-10" data-id="element-201">
                {(t('inclusion.items') as string[]).map((item, i) => <div key={i} className="flex items-start bg-white/60 p-4 rounded-sm" data-id="element-202">
                    <Accessibility className="w-6 h-6 text-brand-terracotta mr-3 flex-shrink-0" data-id="element-203" />
                    <span className="font-medium" data-id="element-204">{item}</span>
                  </div>)}
              </div>
              <p className="text-lg font-medium" data-id="element-205">{t('inclusion.p3')}</p>
            </FadeIn>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-206">
          <div className="max-w-6xl mx-auto" data-id="element-207">
            <FadeIn data-id="element-208">
              <div className="flex items-center justify-center mb-4" data-id="element-209">
                <Trophy className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-210" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-211">
                  {t('testimonials.headline')}
                </h2>
              </div>
              <div className="flex items-center justify-center gap-3 mb-12 flex-wrap" data-id="element-212">
                <div className="flex items-center gap-2 bg-brand-cream px-4 py-2 rounded-sm" data-id="element-213">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-214">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-215" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-216" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-217" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-218" />
                  </svg>
                  <span className="font-medium" data-id="element-219">
                    {t('testimonials.google')}
                  </span>
                </div>
                <div className="flex items-center gap-1" data-id="element-220">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-221" />)}
                </div>
                <span className="font-medium text-lg" data-id="element-222">4.9</span>
                <span className="text-brand-brown/60" data-id="element-223">
                  · {t('testimonials.basedOn')}
                </span>
              </div>
            </FadeIn>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-224">
              {(t('testimonials.items') as any[]).map((review, i) => <FadeIn key={i} delay={i % 3 * 0.15} data-id="element-225">
                  <div className="bg-brand-cream p-6 rounded-sm relative h-full flex flex-col hover:shadow-lg transition-shadow duration-300" data-id="element-226">
                    <div className="flex items-start justify-between mb-4" data-id="element-227">
                      <div className="flex items-center gap-3" data-id="element-228">
                        <div className={`w-11 h-11 rounded-full ${review.color} text-white flex items-center justify-center font-medium text-lg flex-shrink-0`} data-id="element-229">
                          {review.initial}
                        </div>
                        <div data-id="element-230">
                          <p className="font-medium text-brand-brown" data-id="element-231">
                            {review.name}
                          </p>
                          <p className="text-xs text-brand-brown/60" data-id="element-232">
                            {review.time}
                          </p>
                        </div>
                      </div>
                      <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-233">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-234" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-235" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-236" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-237" />
                      </svg>
                    </div>
                    <div className="flex items-center gap-0.5 mb-3" data-id="element-238">
                      {[...Array(review.rating)].map((_, idx) => <Star key={idx} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-239" />)}
                    </div>
                    <p className="text-brand-brown/80 leading-relaxed text-[15px] flex-grow" data-id="element-240">
                      "{review.q}"
                    </p>
                  </div>
                </FadeIn>)}
            </div>
            <FadeIn delay={0.4} data-id="element-241">
              <div className="mt-12 text-center" data-id="element-242">
                <a href="https://www.google.com/search?q=Conexion+Salon+and+Spa+Chicago" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-brand-terracotta hover:text-brand-terracotta-hover font-medium border-b-2 border-brand-terracotta/30 hover:border-brand-terracotta pb-1 transition-colors" data-id="element-243">
                  {t('testimonials.readAll')} <ArrowRight className="w-4 h-4" data-id="element-244" />
                </a>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* WHO IS IT FOR */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-cream" data-id="element-245">
          <div className="max-w-4xl mx-auto" data-id="element-246">
            <FadeIn data-id="element-247">
              <div className="flex items-center justify-center mb-12" data-id="element-248">
                <Target className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-249" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-250">
                  {t('who.headline')}
                </h2>
              </div>
            </FadeIn>
            <div className="grid md:grid-cols-2 gap-12" data-id="element-251">
              <FadeIn delay={0.1} data-id="element-252">
                <div className="bg-white p-8 shadow-sm rounded-sm border-t-4 border-green-500 h-full" data-id="element-253">
                  <h3 className="font-serif text-2xl mb-6" data-id="element-254">{t('who.isFor')}</h3>
                  <ul className="space-y-4" data-id="element-255">
                    {(t('who.forItems') as string[]).map((item, i) => <li key={i} className="flex items-start" data-id="element-256">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-1" data-id="element-257" />
                        <span data-id="element-258">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </FadeIn>
              <FadeIn delay={0.2} data-id="element-259">
                <div className="bg-white p-8 shadow-sm rounded-sm border-t-4 border-red-400 h-full" data-id="element-260">
                  <h3 className="font-serif text-2xl mb-6" data-id="element-261">
                    {t('who.isNotFor')}
                  </h3>
                  <ul className="space-y-4 mb-8" data-id="element-262">
                    {(t('who.notForItems') as string[]).map((item, i) => <li key={i} className="flex items-start" data-id="element-263">
                        <X className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-1" data-id="element-264" />
                        <span data-id="element-265">{item}</span>
                      </li>)}
                  </ul>
                  <p className="text-sm text-brand-brown/60 italic mt-auto" data-id="element-266">
                    {t('who.footer')}
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* OUR PROMISE */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-blue/20" data-id="element-267">
          <div className="max-w-3xl mx-auto text-center" data-id="element-268">
            <FadeIn data-id="element-269">
              <div className="flex items-center justify-center mb-6" data-id="element-270">
                <Lock className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-271" />
                <h2 className="font-serif text-3xl md:text-4xl" data-id="element-272">
                  {t('promise.headline')}
                </h2>
              </div>
              <div className="space-y-6 text-lg" data-id="element-273">
                <p data-id="element-274">{t('promise.p1')}</p>
                <p className="font-medium" data-id="element-275">{t('promise.p2')}</p>
                <p className="font-serif text-2xl italic text-brand-terracotta pt-4" data-id="element-276">
                  {t('promise.p3')}
                </p>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* HOURS & LOCATION */}
        <section id="location" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-277">
          <div className="max-w-4xl mx-auto" data-id="element-278">
            <div className="grid md:grid-cols-2 gap-12" data-id="element-279">
              <FadeIn data-id="element-280">
                <div className="bg-brand-cream p-8 rounded-sm" data-id="element-281">
                  <div className="flex items-center mb-6" data-id="element-282">
                    <Clock className="w-6 h-6 text-brand-terracotta mr-3" data-id="element-283" />
                    <h3 className="font-serif text-2xl" data-id="element-284">
                      {t('hours.headline')}
                    </h3>
                  </div>
                  <div className="space-y-4" data-id="element-285">
                    {(t('hours.items') as any[]).map((item, i) => <div key={i} className="flex justify-between border-b border-brand-brown/10 pb-2" data-id="element-286">
                        <span className="font-medium" data-id="element-287">{item.day}</span>
                        <span data-id="element-288">{item.time}</span>
                      </div>)}
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={0.2} data-id="element-289">
                <div className="bg-brand-cream p-8 rounded-sm h-full flex flex-col justify-center" data-id="element-290">
                  <h3 className="font-serif text-2xl mb-6" data-id="element-291">
                    {t('hours.visitUs')}
                  </h3>
                  <div className="space-y-6" data-id="element-292">
                    <div className="flex items-start" data-id="element-293">
                      <MapPin className="w-6 h-6 text-brand-terracotta mr-4 flex-shrink-0" data-id="element-294" />
                      <span className="text-lg" data-id="element-295">
                        2213 W Roscoe St,
                        <br data-id="element-296" />
                        Chicago, IL 60618
                      </span>
                    </div>
                    <a href="tel:+17732815800" className="flex items-center group bg-brand-terracotta hover:bg-brand-terracotta-hover text-white px-5 py-4 rounded-sm transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-297">
                      <Phone className="w-5 h-5 mr-4 flex-shrink-0 group-hover:animate-pulse" data-id="element-298" />
                      <div className="flex-1" data-id="element-299">
                        <p className="text-xs uppercase tracking-widest opacity-80" data-id="element-300">
                          {t('hours.tapToCall')}
                        </p>
                        <p className="text-lg font-medium" data-id="element-301">773-281-5800</p>
                      </div>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-302" />
                    </a>
                    <div className="flex items-center" data-id="element-303">
                      <Globe className="w-6 h-6 text-brand-terracotta mr-4 flex-shrink-0" data-id="element-304" />
                      <span className="text-lg" data-id="element-305">conexionsalonandspa.com</span>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* HOLOGRAPHIC MAP */}
            <FadeIn delay={0.3} data-id="element-306">
              <div className="mt-12 relative rounded-sm overflow-hidden shadow-2xl bg-brand-brown group" data-id="element-307">
                <div className="relative aspect-[16/9] md:aspect-[21/9] w-full" data-id="element-308">
                  <iframe title="Conexión Salon and Spa Location" src="https://www.openstreetmap.org/export/embed.html?bbox=-87.6890%2C41.9405%2C-87.6770%2C41.9460&amp;layer=mapnik&amp;marker=41.94326%2C-87.68311" className="absolute inset-0 w-full h-full border-0 grayscale-[40%] contrast-110 saturate-75" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-309" />
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-brand-blue/30 via-transparent to-brand-terracotta/20 mix-blend-screen" data-id="element-310"></div>
                  <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay" style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
                  backgroundSize: '40px 40px'
                }} data-id="element-311"></div>
                  <motion.div initial={{
                  y: '-100%'
                }} animate={{
                  y: '100%'
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="absolute inset-x-0 h-32 pointer-events-none" style={{
                  background: 'linear-gradient(to bottom, transparent 0%, rgba(168,206,227,0.0) 40%, rgba(168,206,227,0.6) 50%, rgba(168,206,227,0.0) 60%, transparent 100%)'
                }} data-id="element-312" />
                  {['top-4 left-4 border-t-2 border-l-2', 'top-4 right-4 border-t-2 border-r-2', 'bottom-4 left-4 border-b-2 border-l-2', 'bottom-4 right-4 border-b-2 border-r-2'].map((pos, i) => <div key={i} className={`absolute w-8 h-8 ${pos} border-brand-blue pointer-events-none`} data-id="element-313"></div>)}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-314">
                    <motion.div animate={{
                    scale: [1, 1.8, 1],
                    opacity: [0.6, 0, 0.6]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut'
                  }} className="absolute inset-0 w-16 h-16 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-terracotta" data-id="element-315" />
                    <motion.div animate={{
                    scale: [1, 2.4, 1],
                    opacity: [0.4, 0, 0.4]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut',
                    delay: 0.5
                  }} className="absolute inset-0 w-16 h-16 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-blue" data-id="element-316" />
                    <motion.div animate={{
                    y: [0, -6, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} className="relative w-12 h-12 rounded-full bg-brand-terracotta flex items-center justify-center shadow-2xl ring-4 ring-white/40" data-id="element-317">
                      <MapPin className="w-6 h-6 text-white" data-id="element-318" />
                    </motion.div>
                  </div>
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-brand-brown/70 backdrop-blur-md px-4 py-2 rounded-sm border border-brand-blue/40" data-id="element-319">
                    <motion.div animate={{
                    opacity: [1, 0.3, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} className="w-2 h-2 rounded-full bg-brand-blue" data-id="element-320" />
                    <span className="text-brand-blue text-xs uppercase tracking-[0.2em] font-mono" data-id="element-321">
                      41.9433° N &nbsp;·&nbsp; 87.6831° W
                    </span>
                  </div>
                  <div className="hidden md:block absolute bottom-6 left-6 right-6 md:right-auto md:max-w-sm" data-id="element-322">
                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    delay: 0.5,
                    duration: 0.6
                  }} className="bg-brand-brown/80 backdrop-blur-md p-5 rounded-sm border-l-2 border-brand-blue" data-id="element-323">
                      <p className="text-brand-blue text-xs uppercase tracking-[0.2em] mb-2 font-mono" data-id="element-324">
                        {t('hours.targetLocation')}
                      </p>
                      <h4 className="font-serif text-xl text-white mb-1" data-id="element-325">
                        Conexión Salon and Spa
                      </h4>
                      <p className="text-white/80 text-sm" data-id="element-326">
                        2213 W Roscoe St, Chicago, IL
                      </p>
                      <a href="https://www.google.com/maps/dir/?api=1&destination=2213+W+Roscoe+St,+Chicago,+IL+60618" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 text-brand-blue hover:text-white text-sm font-medium transition-colors" data-id="element-327">
                        {t('hours.getDirections')}{' '}
                        <ArrowRight className="w-4 h-4" data-id="element-328" />
                      </a>
                    </motion.div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* CALL TO ACTION */}
        <section id="book" className="py-24 px-4 sm:px-6 lg:px-8 bg-brand-terracotta text-white text-center" data-id="element-329">
          <div className="max-w-4xl mx-auto" data-id="element-330">
            <FadeIn data-id="element-331">
              <div className="flex items-center justify-center mb-6" data-id="element-332">
                <Megaphone className="w-8 h-8 text-white mr-4" data-id="element-333" />
                <h2 className="font-serif text-4xl md:text-5xl" data-id="element-334">
                  {t('cta.headline')}
                </h2>
              </div>
              <div className="flex items-center justify-center mb-8" data-id="element-335">
                <Star className="w-6 h-6 text-brand-cream mr-3" data-id="element-336" />
                <p className="font-serif text-2xl md:text-3xl italic" data-id="element-337">
                  {t('cta.subheadline')}
                </p>
              </div>
              <p className="text-xl mb-12" data-id="element-338">{t('cta.p1')}</p>
              <p className="text-lg mb-12 max-w-2xl mx-auto opacity-90" data-id="element-339">
                {t('cta.p2')}
              </p>
              <div className="grid gap-4 max-w-2xl mx-auto text-left" data-id="element-340">
                {(t('cta.items') as string[]).map((cta, i) => <button key={i} className="w-full bg-white text-brand-brown hover:bg-brand-cream px-6 py-4 rounded-sm font-medium text-lg transition-colors flex items-center justify-between group shadow-lg" data-id="element-341">
                    <span data-id="element-342">{cta}</span>
                    <ArrowRight className="w-5 h-5 text-brand-terracotta group-hover:translate-x-1 transition-transform" data-id="element-343" />
                  </button>)}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ONE-LINERS */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-brown text-brand-cream overflow-hidden" data-id="element-344">
          <div className="max-w-6xl mx-auto" data-id="element-345">
            <FadeIn data-id="element-346">
              <div className="flex items-center justify-center mb-12" data-id="element-347">
                <Zap className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-348" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-349">
                  {t('oneliners.headline')}
                </h2>
              </div>
            </FadeIn>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-350">
              {(t('oneliners.items') as string[]).map((line, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-351">
                  <div className="border border-brand-cream/20 p-6 rounded-sm h-full flex items-center justify-center text-center hover:bg-brand-cream/5 transition-colors" data-id="element-352">
                    <p className="font-serif text-xl italic" data-id="element-353">"{line}"</p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-cream" data-id="element-354">
          <div className="max-w-3xl mx-auto" data-id="element-355">
            <FadeIn data-id="element-356">
              <div className="flex items-center justify-center mb-12" data-id="element-357">
                <HelpCircle className="w-8 h-8 text-brand-terracotta mr-4" data-id="element-358" />
                <h2 className="font-serif text-3xl md:text-4xl text-center" data-id="element-359">
                  {t('faq.headline')}
                </h2>
              </div>
              <div className="space-y-4" data-id="element-360">
                {faqs.map((faq, i) => <div key={i} className="bg-white rounded-sm shadow-sm overflow-hidden" data-id="element-361">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none" data-id="element-362">
                      <span className="font-medium text-lg pr-4" data-id="element-363">{faq.q}</span>
                      <ChevronDown className={`w-5 h-5 text-brand-terracotta transition-transform ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-364" />
                    </button>
                    <AnimatePresence data-id="element-365">
                      {openFaq === i && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden" data-id="element-366">
                          <div className="px-6 pb-4 pt-2 text-brand-brown/80 border-t border-brand-cream" data-id="element-367">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>)}
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer style={{
      backgroundColor: logoBgColor
    }} className="py-12 px-4 sm:px-6 lg:px-8 border-t border-brand-brown/10" data-id="element-368">
        <div className="max-w-7xl mx-auto flex flex-col items-center text-center" data-id="element-369">
          <img src={LOGO_URL} alt="Conexión Salon and Spa Logo" className="h-20 w-auto object-contain mb-8" data-id="element-370" />
          <div className="space-y-2 mb-8 text-brand-brown/80" data-id="element-371">
            <p data-id="element-372">2213 W Roscoe St, Chicago, IL 60618</p>
            <p data-id="element-373">773-281-5800</p>
            <p data-id="element-374">conexionsalonandspa.com</p>
          </div>
          <p className="text-sm text-brand-brown/60" data-id="element-375">
            © {new Date().getFullYear()} {t('footer.rights')}
          </p>
        </div>
      </footer>

      <BookingToast data-id="element-376" />
    </div>;
}
export function App() {
  const [showIntro, setShowIntro] = useState(true);
  const [showSelector, setShowSelector] = useState(false);
  const handleIntroComplete = () => {
    setShowIntro(false);
    const savedLang = localStorage.getItem('mp_lang');
    if (!savedLang) {
      setShowSelector(true);
    }
  };
  const handleSelectorComplete = () => {
    setShowSelector(false);
  };
  return <LanguageProvider data-id="element-377">
      <AnimatePresence mode="wait" data-id="element-378">
        {showIntro && <IntroAnimation key="intro" onComplete={handleIntroComplete} data-id="element-379" />}
        {!showIntro && showSelector && <LanguageSelector key="selector" onComplete={handleSelectorComplete} data-id="element-380" />}
        {!showIntro && !showSelector && <motion.div key="main" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8
      }} data-id="element-381">
            <MainApp data-id="element-382" />
          </motion.div>}
      </AnimatePresence>
    </LanguageProvider>;
}